/**
 * ProTable 分页请求共享逻辑
 *
 * 被 useTableData 和 useCrudList 共用，消除重复的 request 函数实现。
 * 内部模块，不对外导出。
 */

import type {
  ApiResponse,
  PaginatedData,
  ProTableResponse,
} from '../types/api';
import { isApiSuccess } from '../utils/api-helpers';

export interface TableRequestParams {
  current?: number;
  pageSize?: number;
  [key: string]: unknown;
}

/** 分页请求参数 */
interface FetchPaginatedOptions<T, P> {
  /** ProTable 传入的原始参数 */
  params: TableRequestParams;
  /** 排序信息 */
  sort?: Record<string, 'ascend' | 'descend' | null>;
  /** 筛选信息 */
  filter?: TableRequestParams;
  /** API 请求函数 */
  fetchApi: (params: P) => Promise<ApiResponse<PaginatedData<T>>>;
  /** 成功状态码 */
  successCode: string | number;
  /** 参数转换器 */
  transformParams?: (params: TableRequestParams) => P;
  /** 数据转换器 */
  transformData?: (list: T[]) => T[];
}

/**
 * 执行分页数据请求并转换为 ProTable 响应格式
 *
 * @returns ProTable 标准响应 `{ data, success, total }`
 */
export async function fetchPaginatedData<T, P = Record<string, unknown>>(
  options: FetchPaginatedOptions<T, P>,
): Promise<ProTableResponse<T>> {
  const {
    params,
    sort,
    filter,
    fetchApi,
    successCode,
    transformParams,
    transformData,
  } = options;

  // 解构出 ProTable 特有字段，避免 current 被透传到后端
  const { current, pageSize, ...restParams } = params;

  const apiParams: P = transformParams
    ? transformParams({ ...params, sort, filter })
    : ({
        pageNo: current ?? 1,
        pageSize: pageSize ?? 10,
        ...restParams,
        sort,
        filter,
      } as P);

  const response = await fetchApi(apiParams);
  const isSuccess = isApiSuccess(response, successCode);

  if (isSuccess && response.data) {
    const list = transformData
      ? transformData(response.data.list || [])
      : response.data.list || [];

    return {
      data: list,
      success: true,
      total: response.data.total || 0,
    };
  }

  return { data: [], success: false, total: 0 };
}
